/**
 * La mesure d'audience, en un point.
 *
 * Trois décisions y sont prises une fois pour toutes.
 *
 * 1. **Le relais.** `eu.i.posthog.com` figure dans toutes les listes de
 *    filtrage ; sur un jeu joué au navigateur, ça retire une bonne part des
 *    visites du décompte. On passe donc par `/ph/…`, servi par notre propre
 *    Worker, qui relaie vers PostHog — même domaine, rien à bloquer.
 * 2. **Le chargement différé.** La bibliothèque pèse 85 ko compressés, soit
 *    davantage que le jeu lui-même. La charger avant la première image serait
 *    faire payer la mesure au joueur : elle arrive après, et les évènements
 *    émis entre-temps patientent dans une file.
 * 3. **Aucun secret.** Le jeton de projet part dans chaque requête du
 *    navigateur ; il est public par construction. Le mettre en dur évite un
 *    `.env` pour une valeur que n'importe qui lit dans le code compilé.
 */
type Posthog = typeof import("posthog-js").default

const JETON = "phc_zJFBGKqFE6jgRvcFM9t9PNe9LLb5sc2xBgZRTEmaKvHu"

/** Vrai dès qu'on tourne pour de bon, faux en développement et dans les tests. */
const enProduction = () =>
  location.hostname !== "localhost" && location.hostname !== "127.0.0.1"

let ph: Posthog | null = null
let demarre = false
/** Ce qui est arrivé avant que la bibliothèque ne soit là. */
const file: (() => void)[] = []

const quandPret = (f: (p: Posthog) => void) => {
  if (ph) f(ph)
  else if (demarre) file.push(() => ph && f(ph))
}

export function demarrerMesure(): void {
  if (demarre || !enProduction()) return
  demarre = true
  // `requestIdleCallback` quand il existe : la mesure attend que le jeu ait fini
  // de s'afficher, jamais l'inverse.
  const plusTard = (f: () => void) =>
    "requestIdleCallback" in window
      ? requestIdleCallback(f, { timeout: 4000 })
      : setTimeout(f, 2000)

  plusTard(() => {
    void import("posthog-js").then(({ default: posthog }) => {
      posthog.init(JETON, {
        // Le relais maison. `ui_host` reste le vrai domaine pour que les liens
        // « ouvrir dans PostHog » des enregistrements pointent au bon endroit.
        api_host: `${location.origin}/ph`,
        ui_host: "https://eu.posthog.com",
        defaults: "2025-05-24",
        // Tout ce qu'on peut prendre : les clics, les champs, les clics morts,
        // la performance, et l'enregistrement des sessions. Le jeu ne demande ni
        // adresse ni moyen de paiement — il n'y a pas de donnée sensible à
        // masquer, et voir jouer pour de vrai vaut tous les tableaux de bord.
        autocapture: true,
        capture_pageview: true,
        capture_pageleave: true,
        capture_performance: true,
        capture_dead_clicks: true,
        rageclick: true,
        disable_session_recording: false,
        session_recording: { maskAllInputs: false, maskTextSelector: undefined },
        // Pas de bannière à afficher : aucun cookie n'est déposé. L'identifiant
        // de visiteur vit dans le `localStorage`, comme la sauvegarde du jeu.
        persistence: "localStorage",
        person_profiles: "always",
      })
      ph = posthog
      for (const f of file.splice(0)) f()
    }).catch(() => { /* bloqué ou hors ligne : le jeu n'en dépend pas */ })
  })
}

/** Un évènement métier. Émis tout de suite, ou dès que la mesure est prête. */
export function mesurer(nom: string, props?: Record<string, unknown>): void {
  quandPret((p) => p.capture(nom, props))
}

/**
 * Rattache la session à un compte.
 *
 * L'identifiant est celui que le jeu utilise déjà pour ses sauvegardes : un même
 * joueur retrouvé d'un appareil à l'autre compte pour un.
 */
export function identifier(compte: string, pseudo?: string | null): void {
  quandPret((p) => p.identify(compte, pseudo ? { pseudo } : undefined))
}
